import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import ImagePicker from "@/components/admin/ImagePicker";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const ICONS = ["Waves", "Trees", "Sofa", "Armchair", "Flame", "Music", "Car", "ChefHat", "Sparkles", "Wine", "Droplets", "Projector"];

export default function AmenitiesManager() {
  const [items, setItems] = useState([]);

  const load = () => api.get("/content/amenities").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    try {
      await api.post("/content/amenities", {
        name: "New amenity", icon: "Sparkles", image_url: "", description: "",
        enabled: true, order: items.length,
      });
      toast.success("Added");
      load();
    } catch { toast.error("Could not add"); }
  };

  const save = async (item) => {
    try {
      await api.put(`/content/amenities/${item.id}`, item);
      toast.success("Saved");
    } catch { toast.error("Could not save"); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/content/amenities/${id}`);
      toast.success("Removed");
      load();
    } catch { toast.error("Could not delete"); }
  };

  const upd = (id, field, val) => setItems(items.map((x) => x.id === id ? { ...x, [field]: val } : x));

  return (
    <div data-testid="amenities-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-sage-600">Manage</div>
          <h1 className="font-serif text-4xl text-earth-900 mt-2 font-light">Amenities</h1>
          <p className="text-earth-600 mt-2 max-w-xl">
            Each card on the public site is clickable — visitors see the image and description in a popup.
          </p>
        </div>
        <Button onClick={add} className="bg-sage-500 text-white hover:bg-sage-600 rounded-full" data-testid="add-amenity-btn">
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Add amenity
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {items.map((a) => (
          <div key={a.id} className="bg-white rounded-2xl border border-earth-100 p-6 flex flex-col gap-4" data-testid={`amenity-row-${a.id}`}>
            <div className="grid grid-cols-12 gap-3 items-start">
              <Input className="col-span-7" value={a.name} onChange={(e) => upd(a.id, "name", e.target.value)} placeholder="Name" />
              <select
                className="col-span-3 bg-white border border-earth-200 rounded-md px-3 py-2 text-sm"
                value={a.icon}
                onChange={(e) => upd(a.id, "icon", e.target.value)}
              >
                {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
              <Input type="number" className="col-span-2" value={a.order} onChange={(e) => upd(a.id, "order", Number(e.target.value))} placeholder="Order" />
            </div>

            <ImagePicker
              label="Image (shown in popup)"
              value={a.image_url}
              onChange={(v) => upd(a.id, "image_url", v)}
              testId={`amenity-img-${a.id}`}
              aspect="aspect-video"
            />

            <Textarea
              rows={2}
              value={a.description || ""}
              onChange={(e) => upd(a.id, "description", e.target.value)}
              placeholder="Short description (shown in popup)"
            />

            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm">
                <Switch checked={a.enabled !== false} onCheckedChange={(v) => upd(a.id, "enabled", v)} />
                <span className="text-earth-700">{a.enabled !== false ? "Visible" : "Hidden"}</span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => save(a)} className="bg-earth-900 text-white hover:bg-earth-800 rounded-full" data-testid={`amenity-save-${a.id}`}>
                  <Save className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Save
                </Button>
                <Button size="icon" variant="ghost" onClick={() => del(a.id)} data-testid={`amenity-del-${a.id}`}>
                  <Trash2 className="w-4 h-4 text-red-600" strokeWidth={1.5} />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {items.length === 0 && <div className="p-8 text-center text-earth-500 mt-6">No amenities yet.</div>}
    </div>
  );
}
